import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import Header from "../../components/Header";
import LoadingSpinner from "../../loadingSpinner";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getOrdenesCarga, patchOrdenCargaStatus } from "../../services/ordenCarga.services";

const STATUS_OPTIONS = [
  "AGUARDANDO_CARREGAMENTO",
  "EN_CARGA",
  "CERRADA",
  "ANULADA",
];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatFecha = (value) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const STATUS_COLORS = {
  AGUARDANDO_CARREGAMENTO: "#f5c518",
  EN_CARGA: "#42a5f5",
  CERRADA: "#4caf50",
  ANULADA: "#ef5350",
};

const OrdenCargaListado = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [ordenes, setOrdenes] = useState([]);
  const [selectedOrdenId, setSelectedOrdenId] = useState(null);
  const [nuevoStatus, setNuevoStatus] = useState("AGUARDANDO_CARREGAMENTO");
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    try {
      const data = await getOrdenesCarga();
      setOrdenes(
        Array.isArray(data)
          ? data.map((orden, index) => ({
              ...orden,
              id: orden?.id ?? `orden-${index}`,
              nro_orden: orden?.nro_orden || "-",
              status: orden?.status || "AGUARDANDO_CARREGAMENTO",
              motorista_nombre: orden?.motorista_nombre || "-",
              total_paquetes: toNumber(orden?.total_paquetes),
              total_chapas: toNumber(orden?.total_chapas),
              peso_total_con_tara_kg: toNumber(orden?.peso_total_con_tara_kg),
            }))
          : []
      );
    } catch (error) {
      toast.error(error.message || "No se pudieron cargar las órdenes");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleActualizarEstado = async () => {
    if (!selectedOrdenId) {
      toast.warning("Seleccione una orden de la tabla");
      return;
    }
    setIsLoading(true);
    try {
      await patchOrdenCargaStatus(selectedOrdenId, nuevoStatus);
      toast.success("Estado actualizado correctamente");
      setSelectedOrdenId(null);
      await fetchData();
    } catch (error) {
      toast.error(error.message || "No se pudo actualizar el estado");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.25 },
    { field: "nro_orden", headerName: "Nro Orden", flex: 0.7 },
    {
      field: "fecha_carga",
      headerName: "Fecha",
      flex: 0.5,
      valueFormatter: ({ value }) => formatFecha(value),
    },
    { field: "motorista_nombre", headerName: "Motorista", flex: 0.8 },
    {
      field: "status",
      headerName: "Estado",
      flex: 0.9,
      renderCell: ({ value }) => (
        <Box
          px={1}
          py={0.3}
          borderRadius={1}
          sx={{
            backgroundColor: `${STATUS_COLORS[value] ?? "#888"}22`,
            color: STATUS_COLORS[value] ?? "#888",
          }}
        >
          <Typography variant="caption" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      ),
    },
    { field: "total_paquetes", headerName: "Paquetes", flex: 0.4, type: "number" },
    { field: "total_chapas", headerName: "Chapas", flex: 0.4, type: "number" },
    {
      field: "peso_total_con_tara_kg",
      headerName: "Peso Total c/Tara (kg)",
      flex: 0.8,
      valueGetter: (params) =>
        toNumber(params.row.peso_total_con_tara_kg).toLocaleString("es-PY"),
    },
    {
      field: "acciones",
      headerName: "Detalle",
      flex: 0.4,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => navigate(`/view/ordenCarga/${params.row.id}`)}
          sx={{ fontSize: "0.7rem" }}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px">
      {isLoading && <LoadingSpinner />}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header
          title="Órdenes de Carga"
          subtitle="Listado completo de órdenes registradas"
        />
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => navigate("/view/ordenCarga")}
        >
          Nueva Orden
        </Button>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        p={2}
        sx={{ backgroundColor: colors.primary[400], borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ mr: 1 }}>
          {selectedOrdenId
            ? `Orden seleccionada: #${selectedOrdenId}`
            : "Haz clic en una fila para seleccionarla"}
        </Typography>
        <TextField
          select
          size="small"
          label="Nuevo estado"
          value={nuevoStatus}
          onChange={(e) => setNuevoStatus(e.target.value)}
          sx={{ minWidth: 240 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<AutorenewIcon />}
          onClick={handleActualizarEstado}
          disabled={!selectedOrdenId}
        >
          Actualizar estado
        </Button>
      </Box>

      <Box
        height="72vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: `${colors.greenAccent[900]} !important`,
          },
        }}
      >
        <DataGrid
          rows={ordenes}
          columns={columns}
          disableSelectionOnClick
          onRowClick={(params) => setSelectedOrdenId(params.row.id)}
          pageSize={15}
          rowsPerPageOptions={[15, 30, 50]}
        />
      </Box>
    </Box>
  );
};

export default OrdenCargaListado;
